import {
  type MessagePathNode,
  RepresentationStorageFormat,
} from "@/shared/domain/topics";

/**
 * The MCAP schema names a `foxglove.PointCloud` is published under: the
 * protobuf/JSON name, the ROS1 and ROS2 message names, and the OMG IDL name.
 * Foxglove recognises exactly these, and so does the decoder.
 */
export const POINT_CLOUD_SCHEMA_NAMES: ReadonlySet<string> = new Set([
  "foxglove.PointCloud",
  "foxglove_msgs/PointCloud",
  "foxglove_msgs/msg/PointCloud",
  "foxglove::PointCloud",
]);

/**
 * Whether a message path is the `data` bytes of a PointCloud topic the panel
 * can read: a top-level field named `data`, on a topic whose schema is one of
 * {@link POINT_CLOUD_SCHEMA_NAMES}, with a representation the worker can read
 * messages from — MCAP, or the Parquet a protobuf recording is ingested to.
 *
 * The bound path has to be `data` itself: the worker asks the reader for the
 * bound path, as bytes, beside the layout columns, so bound to any other path
 * of the topic a Parquet read hands back no `data` to decode.
 */
export function isPointCloudDataPath(node: MessagePathNode): boolean {
  return (
    node.data.path_in_schema.length === 1 &&
    node.data.path_in_schema[0] === "data" &&
    POINT_CLOUD_SCHEMA_NAMES.has(node.topic.data.schema_name ?? "") &&
    (node.hasRepresentation(RepresentationStorageFormat.MCAP) ||
      node.hasRepresentation(RepresentationStorageFormat.PARQUET))
  );
}
